import { Auction } from '../types/auction.types';
import { toDate } from './lotTiming';
import { lotEndTime, normalizeStatus } from './saleState';

/**
 * WHICH SALE AM I WORKING ON — one definition, two derivations.
 *
 * 🔴 Why this exists: every operator page invented its own answer, and they had
 * drifted into four incompatible ones:
 *
 *   ShippingStation   a hardcoded auction id, written in June, still June in August
 *   PickerInterface   first result of orderBy('createdAt','desc')
 *   PickupOrders      the same code, copied
 *   InvoiceList       the same code, copied again, as a fallback
 *   AdminBids         client-side sort on endDate desc, with its own private toDate
 *
 * Three of those are the same wrong key and one is a different wrong key. This
 * module replaces all five. Same discipline as DECK_TYPES and lotImages: one
 * source, everything else derives.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NOT `createdAt`
 *
 * It records when the RECORD was typed in, not when the sale ran. A sale built
 * today for next month outranks the sale that closed last night — which is how
 * the Picker ended up defaulting to a LiveAuctioneers import from months back.
 *
 * It is also the more dangerous of the two, because `orderBy('createdAt')` in
 * Firestore does not sort documents missing the field to the end — it OMITS
 * them. An auction without `createdAt` is absent from those dropdowns entirely,
 * with nothing on screen to say so. Nothing here orders on createdAt, so a sale
 * missing it still appears and can still be chosen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NOT `endDate`
 *
 * Because soft close makes it a lie. `endDate` is the SCHEDULED end; extensions
 * push the real close past it. saleState.ts exists for exactly this reason — the
 * homepage used to blank at 8 PM while lots were still open. The honest key is
 * the last lot to close.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY TWO DERIVATIONS
 *
 * "The most recent sale" and "the sale I'm working on" come apart during
 * closedown week, and picking either one alone is wrong half the time:
 *
 *   Sunday's sale is a draft being built; the inaugural sale closed Saturday and
 *   is the one being invoiced, picked and shipped.
 *     → drafts are excluded, so both derivations return the inaugural sale. ✅
 *
 *   Sunday goes live. currentSale follows it (that IS the sale in play). But a
 *   live sale has no invoices yet, so pointing the Shipping Station at it would
 *   be a fresh way of being wrong.
 *     → currentFulfillmentSale stays on the inaugural sale until Sunday closes. ✅
 *
 * One ranking, one query, two named answers. A page picks the one that matches
 * what it is for — it never invents a third.
 */

/** How a sale's effective end was determined. Surfaced so a weak ranking is visible. */
export type EndSource = 'lots' | 'endDate' | 'none';

export interface RankedSale {
  auction: Auction;
  id: string;
  title: string;
  /** ms since epoch of the LAST lot to close, or null if unknowable. */
  effectiveEndMs: number | null;
  endSource: EndSource;
  isDraft: boolean;
  hasStarted: boolean;
  hasEnded: boolean;
}

/** Roles a page can ask for. Add one only with a reason; two is the point. */
export type SaleRole = 'current' | 'fulfillment';

/**
 * The real end of a sale: the last lot to close.
 *
 * Per lot that is `actualEndTime || scheduledEndTime` (saleState.lotEndTime —
 * reused rather than re-derived, so soft-close semantics can't drift). Falls
 * back to the auction's own `endDate` ONLY when no lot carries a time at all,
 * which is the case for imported sales that never had lot-level timing.
 */
export const effectiveEndMs = (auction: Auction): { ms: number | null; source: EndSource } => {
  let last: number | null = null;
  for (const lot of auction?.lots || []) {
    const end = lotEndTime(lot);
    if (!end) continue;
    const t = end.getTime();
    if (last === null || t > last) last = t;
  }
  if (last !== null) return { ms: last, source: 'lots' };

  const fallback = toDate((auction as any)?.endDate);
  if (fallback) return { ms: fallback.getTime(), source: 'endDate' };

  return { ms: null, source: 'none' };
};

/** A draft is never operational — it is a sale being built, not one being worked. */
const isDraftSale = (auction: Auction): boolean => normalizeStatus(auction?.status) === 'draft';

/** Absent a start date, assume it started — an old sale with no startDate is still real. */
const hasStartedBy = (auction: Auction, nowMs: number): boolean => {
  const start = toDate((auction as any)?.startDate);
  return start ? start.getTime() <= nowMs : true;
};

/**
 * Every sale that could sensibly be defaulted to, newest real end first.
 *
 * Excluded: drafts, sales that have not started, and sales with no derivable end
 * (ranking them would be guessing). They stay available in the SELECTOR — this
 * governs the default only. Nothing is ever hidden from the operator.
 */
export const rankSales = (auctions: Auction[], nowMs: number = Date.now()): RankedSale[] => {
  const rows: RankedSale[] = [];
  for (const auction of auctions || []) {
    if (!auction) continue;
    const { ms, source } = effectiveEndMs(auction);
    rows.push({
      auction,
      id: auction.id,
      title: auction.title || auction.id,
      effectiveEndMs: ms,
      endSource: source,
      isDraft: isDraftSale(auction),
      hasStarted: hasStartedBy(auction, nowMs),
      hasEnded: ms !== null && ms <= nowMs,
    });
  }
  return rows
    .filter((r) => !r.isDraft && r.hasStarted && r.effectiveEndMs !== null)
    // Ties broken by id so the order is deterministic across reloads — two sales
    // ending in the same second must not swap places between renders.
    .sort((a, b) => (b.effectiveEndMs! - a.effectiveEndMs!) || a.id.localeCompare(b.id));
};

/**
 * Order for a LIST view — "show me every sale I have".
 *
 * Different job from rankSales, and the difference matters. rankSales EXCLUDES
 * drafts and unstarted sales because they must never win a default. A list must
 * never DROP anything, so this keeps every sale and only orders them.
 *
 * Ordered by the real end, newest first, which puts a draft being built for next
 * Sunday at the top where a seller wants it, then the sale that just closed,
 * then older ones. Sales with no derivable end sort last rather than being
 * treated as ancient.
 *
 * 🔴 This exists because the seller list views used orderBy('createdAt','desc'),
 * and Firestore does not sort documents missing the ordered field to the end —
 * it OMITS them. A sale without a createdAt was absent from the Dashboard and
 * the Auctions list entirely, which is worse than being mis-ranked in the
 * Picker: those two pages are how you find out a sale exists at all.
 */
export const sortSalesForListing = <T extends Auction>(auctions: T[]): T[] =>
  [...(auctions || [])].filter(Boolean).sort((a, b) => {
    const ea = effectiveEndMs(a).ms;
    const eb = effectiveEndMs(b).ms;
    if (ea === null && eb === null) return (a.title || a.id).localeCompare(b.title || b.id);
    if (ea === null) return 1;      // unknowable ends go last, not first
    if (eb === null) return -1;
    return (eb - ea) || String(a.id).localeCompare(String(b.id));
  });

/**
 * The sale in play: most recent by real end, drafts and unstarted excluded.
 * For consoles, dashboards, bid admin — anywhere "what's happening now" is meant.
 */
export const currentSale = (auctions: Auction[], nowMs: number = Date.now()): Auction | null =>
  rankSales(auctions, nowMs)[0]?.auction ?? null;

/**
 * The sale being worked DOWNSTREAM of close: the most recent one that has
 * actually finished. For the Picker, Pickup Orders, the Shipping Station and the
 * Invoice List — pages whose work only exists once there are invoices.
 *
 * Deliberately does NOT follow a live sale. During Sunday's auction the boxes on
 * the bench are still Saturday's.
 */
export const currentFulfillmentSale = (auctions: Auction[], nowMs: number = Date.now()): Auction | null =>
  rankSales(auctions, nowMs).find((r) => r.hasEnded)?.auction ?? null;

/** Resolve by role. The only entry point pages should need. */
export const resolveSale = (
  auctions: Auction[],
  role: SaleRole,
  nowMs: number = Date.now(),
): Auction | null =>
  role === 'fulfillment' ? currentFulfillmentSale(auctions, nowMs) : currentSale(auctions, nowMs);

/**
 * Why this sale, in words, for the banner's tooltip. An operator who disagrees
 * with the default should be able to see the reasoning without reading the code.
 */
export const explainResolution = (
  auctions: Auction[],
  role: SaleRole,
  nowMs: number = Date.now(),
): string => {
  const ranked = rankSales(auctions, nowMs);
  if (!ranked.length) return 'No sale qualifies: every sale is a draft, unstarted, or has no end time.';
  const chosen = role === 'fulfillment' ? ranked.find((r) => r.hasEnded) : ranked[0];
  if (!chosen) return 'No sale has closed yet, so there is no fulfilment work to default to.';

  const when = new Date(chosen.effectiveEndMs!).toLocaleString();
  const basis = chosen.endSource === 'lots' ? 'its last lot closed' : 'its scheduled end (no lot times on record)';
  const note = role === 'fulfillment' && ranked[0] && ranked[0].id !== chosen.id
    ? ` ${ranked[0].title} is more recent but has not closed, so fulfilment stays here.`
    : '';
  return `${chosen.title} — ${basis} ${when}.${note}`;
};
